<template>
  <RouterView />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useColors } from 'vuestic-ui'
import { isAdminAuthenticated, setSessionExpiredCallback, validateAdminSessionFromActivity } from './services/repodepot-api'
import { applyRdThemeDocumentState, normalizeRdThemePreset, RD_THEME_STORAGE_KEY } from './services/rd-theme'

const router = useRouter()
const SESSION_ACTIVITY_CHECK_INTERVAL_MS = 15_000
const SESSION_ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'focus'] as const
const SESSION_VISIBILITY_EVENT = 'visibilitychange'
const { applyPreset } = useColors()
let lastSessionActivityCheckAt = 0
let sessionActivityCheckInFlight = false

const isAuthRouteActive = () => router.currentRoute.value.path.startsWith('/auth')

const redirectToLogin = () => {
  if (!isAuthRouteActive()) {
    void router.push({ name: 'login' })
  }
}

const handleSessionActivity = () => {
  if (document.visibilityState === 'hidden' || isAuthRouteActive() || !isAdminAuthenticated()) {
    return
  }

  const now = Date.now()
  if (sessionActivityCheckInFlight || now - lastSessionActivityCheckAt < SESSION_ACTIVITY_CHECK_INTERVAL_MS) {
    return
  }

  lastSessionActivityCheckAt = now
  sessionActivityCheckInFlight = true
  validateAdminSessionFromActivity()
    .catch(() => true)
    .finally(() => {
      sessionActivityCheckInFlight = false
    })
}

onMounted(() => {
  const stored = localStorage.getItem(RD_THEME_STORAGE_KEY)
  const preset = normalizeRdThemePreset(stored)
  applyPreset(preset)
  applyRdThemeDocumentState(preset)

  if (stored !== preset) {
    localStorage.setItem(RD_THEME_STORAGE_KEY, preset)
  }

  setSessionExpiredCallback(redirectToLogin)
  SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleSessionActivity, { passive: true })
  })
  document.addEventListener(SESSION_VISIBILITY_EVENT, handleSessionActivity)
})

onBeforeUnmount(() => {
  SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, handleSessionActivity)
  })
  document.removeEventListener(SESSION_VISIBILITY_EVENT, handleSessionActivity)
})
</script>

<style lang="scss">
#app {
  font-family: 'Inter', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  margin: 0;
  min-width: 20rem;
}
</style>
